import type { Connector } from '../shapes/Connector';
import type { Shape } from '../shapes/Shape';
import type { TextElement } from '../shapes/TextElement';


export type DiagramBounds = { minX: number; minY: number; maxX: number; maxY: number };

export type DiagramBoundsInput = {
  shapes?: Shape[];
  connectors?: Connector[];
  textElements?: TextElement[];
  measureCtx: CanvasRenderingContext2D;
};

export const getDiagramBounds = ({
  shapes = [],
  connectors = [],
  textElements = [],
  measureCtx,
}: DiagramBoundsInput): DiagramBounds | null => {
  if (shapes.length === 0 && connectors.length === 0 && textElements.length === 0) {
    return null;
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  const updateBounds = (x: number, y: number): void => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  };

  for (const shape of shapes) {
    const bounds = shape.getBounds();
    updateBounds(bounds.x, bounds.y);
    updateBounds(bounds.x + bounds.width, bounds.y + bounds.height);
  }

  for (const connector of connectors) {
    const points = connector.getAllPoints();
    if (connector.arcPoint) {
      points.push(connector.arcPoint);
    }
    for (const point of points) {
      updateBounds(point.x, point.y);
    }
  }

  for (const textElement of textElements) {
    const bounds = textElement.getBounds(measureCtx);
    updateBounds(bounds.x, bounds.y);
    updateBounds(bounds.x + bounds.width, bounds.y + bounds.height);
  }

  if (minX === Infinity) return null;
  return { minX, minY, maxX, maxY };
};
